import React, { SyntheticEvent, useCallback, useState, useEffect } from 'react';
import cl from 'classnames';
import map from 'lodash/map';
import some from 'lodash/some';

import type {
  GlobalCompanyTabCompanyType,
  GlobalCompanyTabNanoID
} from '../../../globalCompanyTabsTypes';
import { TeamNanoID } from '../../../../teams/teamsTypes';

import {
  FETCH_GLOBAL_COMPANY_TABS_QUERY,
  FetchGlobalCompanyTabsQueryResponse
} from '../../../queries/fetchGlobalCompanyTabs.query';

import { useGlobalCompanyTabs } from '../../../hooks/useGlobalCompanyTabs';

import { GlobalCompanyTabsMenuDropdownItem } from '../GlobalCompanyTabsMenuDropdownItem';
import { CreateGlobalCompanyTabModalButton } from '../../modalButtons/CreateGlobalCompanyTabModalButton';

import { ButtonHelper } from '../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../helpers/Icon';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';
import { Translate } from '../../../../../helpers/Translate';

import { GlobalCompanyTabsCache } from '../../../GlobalCompanyTabsCache';
import { words } from '../../../../../locales/keys';

import { IconsEnum } from '../../../../../assets/icons/types';

interface GlobalCompanyTabsMenuDropdownProps {
  activeTabNanoId?: GlobalCompanyTabNanoID;
  companyType: GlobalCompanyTabCompanyType;
  companyNanoId: TeamNanoID;
  onDeleteActiveTab?: () => void;
  isMenuOpen?: boolean;
  withCreateButton: boolean;
  withUpdateButton: boolean;
  withDeleteButton: boolean;
}

function GlobalCompanyTabsMenuDropdown({
  activeTabNanoId,
  companyType,
  companyNanoId,
  onDeleteActiveTab,
  isMenuOpen = false,
  withCreateButton,
  withUpdateButton,
  withDeleteButton
}: GlobalCompanyTabsMenuDropdownProps) {
  const { globalCompanyTabs, globalCompanyTabsFetched } =
    useGlobalCompanyTabs<FetchGlobalCompanyTabsQueryResponse>({
      cacheKey: GlobalCompanyTabsCache.indexCacheKey(),
      query: FETCH_GLOBAL_COMPANY_TABS_QUERY,
      initialFilters: { companyType: { eq: companyType } }
    });

  const [isOpen, setIsOpen] = useState<boolean>(isMenuOpen);

  useEffect(() => {
    if (!isMenuOpen) {
      setIsOpen(some(globalCompanyTabs, ['nanoId', activeTabNanoId]));
    }
  }, [activeTabNanoId, isMenuOpen, globalCompanyTabs]);

  const handleClick = useCallback<(e: SyntheticEvent) => void>(
    (e: SyntheticEvent) => {
      e.preventDefault();
      setIsOpen((prevVal) => !prevVal);
    },
    [setIsOpen]
  );

  return (
    <div className="-mx-4">
      <div className="flex items-center group relative px-4 py-2 transition-colors ease-in-out duration-150 focus-within:ring-2 focus-within:ring-gray-850 dark:focus-within:ring-gray-100 bg-gray-500 bg-opacity-0 hover:bg-opacity-20">
        <div className="pl-2 pr-1">
          <Icon
            icon={IconsEnum.VIEW_COLUMNS}
            className="h-5 w-5 flex-shrink-0"
          />
        </div>
        <div className="absolute left-1 inset-y-0 flex items-center">
          <Icon
            icon={IconsEnum.CHEVRON_RIGHT}
            className={cl(
              'text-current h-5 w-5 transform flex-shrink-0',
              isOpen ? 'rotate-90' : null
            )}
          />
        </div>
        <ButtonHelper
          className="flex-1 px-2 text-sm text-left focus:ring-0 focus:ring-offset-0"
          onClick={handleClick}
        >
          <Translate id={words.customTabs} />
          <span className="absolute inset-0" aria-hidden="true" />
        </ButtonHelper>
      </div>

      {isOpen ? (
        <LoadingSkeleton count={2} loaded={globalCompanyTabsFetched}>
          <ul className="mt-2 mb-4 px-4">
            {globalCompanyTabs?.map(({ name, nanoId, uuid, roles }) => (
              <GlobalCompanyTabsMenuDropdownItem
                active={activeTabNanoId === nanoId}
                name={name}
                key={nanoId}
                onDeleteActiveTab={onDeleteActiveTab}
                globalCompanyTabNanoId={nanoId}
                globalCompanyTabUuid={uuid}
                globalCompanyTabRoleIds={map(roles, 'id')}
                companyNanoId={companyNanoId}
                withUpdateButton={withUpdateButton}
                withDeleteButton={withDeleteButton}
              />
            ))}

            {withCreateButton && (
              <li className="bg-gray-500 duration-150 ease-in-out flex group items-center py-2 relative transition-colors -mx-4 pl-6 pr-4 bg-opacity-0 hover:bg-opacity-20">
                <CreateGlobalCompanyTabModalButton
                  companyType={companyType}
                  icon={IconsEnum.PLUS}
                  iconClassName="h-5 w-5"
                  className="grow flex items-center justify-center py-0.5 rounded text-gray-500 transition-colors group-hover:text-gray-950 dark:group-hover:text-white focus:ring-offset-0"
                />
              </li>
            )}
          </ul>
        </LoadingSkeleton>
      ) : null}
    </div>
  );
}

export default GlobalCompanyTabsMenuDropdown;
